import axios from 'axios'
import type {AxiosResponse, InternalAxiosRequestConfig} from 'axios';
import { getAccessToken, isAuthenticated } from '@/lib/auth/authSession'
import { performLogout } from '@/lib/auth/performLogout'
import { attemptTokenRefresh, getAccessTokenExpiryMs } from '@/lib/auth/tokenRefresh'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'
import { isNetworkError, markApiDown, markApiHealthy, probeApiReachable } from '@/lib/api/apiHealth'

// Extend Axios types to include metadata and retry flag
declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    metadata?: {
      startTime: number
    }
    _retried?: boolean
  }
}

// Create a new axios instance
const authAxios = axios.create({
  baseURL: import.meta.env.VITE_MOLY_API,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add request interceptor
authAxios.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // The cached Better Auth JWT (see lib/auth/authSession.ts)
    const accessToken = getAccessToken()

    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`
    }

    // Add request timestamp for duration tracking
    config.metadata = { startTime: performance.now() }

    // Log API request
    logger.logApiRequest(config.method?.toUpperCase() || 'GET', config.url || '')

    return config
  },
  (error) => {
    // Handle request error here
    logger.logError(error, LogCategory.API_ERROR, {
      phase: 'request',
    })
    return Promise.reject(error)
  },
)

// Add response interceptor
authAxios.interceptors.response.use(
  (response: AxiosResponse) => {
    // Any status code within the range of 2xx will trigger this function

    // Calculate request duration
    const duration = response.config.metadata?.startTime
      ? performance.now() - response.config.metadata.startTime
      : 0

    // Log successful API response
    logger.logApiResponse(
      response.config.method?.toUpperCase() || 'GET',
      response.config.url || '',
      response.status,
      duration
    )

    markApiHealthy()

    return response
  },
  async (error) => {
    // Network-level failure: API unreachable. Mark status and reject without
    // touching auth state — a network 401 is not a real auth rejection.
    if (isNetworkError(error)) {
      markApiDown()
      logger.logEvent(LogCategory.API_ERROR, 'api.network_unreachable', {
        url: error.config?.url,
        code: error.code,
      })
      return Promise.reject(error)
    }
    // Calculate request duration even for errors
    const duration = error.config?.metadata?.startTime
      ? performance.now() - error.config.metadata.startTime
      : 0

    // Log API error
    logger.logApiError(
      error.config?.method?.toUpperCase() || 'GET',
      error.config?.url || 'unknown',
      error,
      {
        duration,
        status: error.response?.status,
      }
    )

    // Handle errors here (e.g., logging, redirecting to login on 401)
    if (error.response?.status === 401) {
      // Prevent infinite retry loop: only attempt refresh once per request
      if (error.config?._retried) {
        // Already retried after refresh — don't retry again
        if (!(await probeApiReachable())) {
          markApiDown()
          logger.logEvent(LogCategory.AUTH_SESSION, 'retry.401.api_unreachable_hold_session', {
            url: error.config?.url,
          })
          return Promise.reject(error)
        }
        await performLogout()
        window.location.href = '/auth'
        return Promise.reject(error)
      }

      // Handle unauthorized access - token expired or invalid
      const currentToken = getAccessToken()
      const hadSession = isAuthenticated()
      const expiresAtMs = getAccessTokenExpiryMs(currentToken)
      const timeRemainingMs = expiresAtMs ? expiresAtMs - Date.now() : null

      logger.logEvent(LogCategory.AUTH_SESSION, 'token.expired.attempting_refresh', {
        url: error.config?.url,
        hasAccessToken: !!currentToken,
        hasSession: hadSession,
        tokenExpired: timeRemainingMs != null ? timeRemainingMs <= 0 : null,
        timeRemainingMs,
        expiresAt: expiresAtMs ? new Date(expiresAtMs).toISOString() : 'unknown',
      })

      // Attempt to refresh the token before giving up
      const refreshed = await attemptTokenRefresh()

      if (refreshed && error.config) {
        // Retry the original request with the new token
        const newToken = getAccessToken()
        const newExpiresAtMs = getAccessTokenExpiryMs(newToken)
        logger.logEvent(LogCategory.AUTH_SESSION, 'refresh.401.retry', {
          url: error.config?.url,
          hasNewToken: !!newToken,
          newExpiresAt: newExpiresAtMs ? new Date(newExpiresAtMs).toISOString() : 'unknown',
        })
        if (newToken) {
          error.config.headers['Authorization'] = `Bearer ${newToken}`
          error.config._retried = true
          return authAxios.request(error.config)
        }
      }

      // Refresh failed - before tearing down the session, confirm the API is
      // actually reachable. If it's down, the refresh likely failed due to
      // network and we should hold the session instead of forcing re-login.
      if (!(await probeApiReachable())) {
        markApiDown()
        logger.logEvent(LogCategory.AUTH_SESSION, 'refresh.failed.api_unreachable_hold_session', {
          url: error.config?.url,
        })
        return Promise.reject(error)
      }

      logger.logEvent(LogCategory.AUTH_SESSION, 'expired', {
        url: error.config?.url,
        refreshAttempted: true,
        refreshSucceeded: refreshed,
        hadSession,
      })

      // Revoke + clear the auth session, per-user stores, RxDB, and app state
      await performLogout()

      // Redirect to auth gateway (user can sign in again)
      window.location.href = '/auth'
    }
    return Promise.reject(error)
  },
)

export default authAxios
